import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { extractErrorMessage } from "@/lib/apiError";
import { MailCheck, MailWarning, Loader2 } from "lucide-react";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data.token) localStorage.setItem("session_token", data.token);
        setUser(data.user);
        // Re-run the auth check with the fresh bearer token so any in-flight
        // 401 from the initial mount can't overwrite the user back to null.
        await checkAuth();
        setStatus("success");
        toast.success("Email verified — welcome to Clinq!");
        setTimeout(() => navigate("/dashboard"), 1800);
      } catch (err) {
        setError(extractErrorMessage(err, "Verification failed"));
        setStatus("error");
      }
    })();
  }, [token, navigate, setUser, checkAuth]);

  const resend = async (e) => {
    e.preventDefault();
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail });
      setResent(true);
      toast.success("If that email has an unverified account, a fresh link is on its way.");
    } catch {
      toast.error("Could not send the email. Please try again.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-white"
      style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="verify-email-page">
      <SEO title="Verify your email" path="/verify-email" noindex />
      <div className="w-full max-w-md rounded-2xl p-8 border text-center"
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>

        {status === "verifying" && (
          <div data-testid="verify-email-verifying">
            <Loader2 size={40} className="mx-auto animate-spin" style={{ color: C.teal }} />
            <h1 className="font-extrabold text-2xl mt-5 tracking-tight">Verifying your email…</h1>
            <p className="mt-2 text-sm text-white/60">Hang tight, this only takes a second.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-email-success">
            <MailCheck size={40} className="mx-auto" style={{ color: C.tealSoft }} />
            <h1 className="font-extrabold text-2xl mt-5 tracking-tight">Email verified!</h1>
            <p className="mt-2 text-sm text-white/60">
              You're all set. Taking you to your dashboard…
            </p>
            <Link to="/dashboard" data-testid="verify-email-go-dashboard"
              className="inline-block mt-6 px-5 py-2.5 rounded-md font-semibold text-sm"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Go to dashboard
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-email-error">
            <MailWarning size={40} className="mx-auto" style={{ color: C.orange }} />
            <h1 className="font-extrabold text-2xl mt-5 tracking-tight">Verification failed</h1>
            <p className="mt-2 text-sm text-white/60" data-testid="verify-email-error-msg">{error}</p>

            {resent ? (
              <p className="mt-6 text-sm font-semibold" style={{ color: C.tealSoft }} data-testid="verify-email-resent-note">
                Fresh verification email sent — check your inbox.
              </p>
            ) : (
              <form onSubmit={resend} className="mt-6 space-y-3">
                <input
                  type="email" required value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="founder@yourco.com"
                  data-testid="verify-email-resend-input"
                  className="w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
                <button type="submit" disabled={resendBusy} data-testid="verify-email-resend-btn"
                  className="w-full py-2.5 rounded-md font-semibold text-sm disabled:opacity-60"
                  style={{ backgroundColor: C.teal, color: "#fff" }}>
                  {resendBusy ? "Sending…" : "Resend Verification Email"}
                </button>
              </form>
            )}

            <div className="mt-5 text-sm text-white/60">
              <Link to="/login" data-testid="verify-email-to-login" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
                Back to log in
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
